import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUser, FiMapPin, FiPhone, FiMail, FiCalendar, FiImage, 
  FiSave, FiUserCheck, FiHeart, FiGlobe, FiMenu, FiX,
  FiUmbrella, FiSettings, FiCheckCircle, FiAlertCircle
} from 'react-icons/fi';
import { updateUserProfile,fetchUserProfile } from '../api'; // Assuming you have this API call
import { useAuth } from '../context/AuthContext';


// Import the LeaveApplicationForm component
import LeaveApplicationForm from './LeaveApplicationForm';

// Helper function to format date for input[type="date"]
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const BASE_BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AvatarStyle = () =>{
  const [currentAvatar, setCurrentAvatar] = useState(null);
  const [selected, setSelected] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const { fetchUser } = useAuth();

  const avatarList = [
    ...Array.from({ length: 5 }, (_, i) => {
      const fileName = `boy${i + 1}`;
      return {
        name: fileName,
        pngSrc: `${BASE_BACKEND_URL}/static/avatars/${fileName}.png`,

      };
    }),
    ...Array.from({ length: 5 }, (_, i) => {
      const fileName = `girl${i+1}`;
      return {
        name: fileName,
        pngSrc: `${BASE_BACKEND_URL}/static/avatars/${fileName}.png`,

      };
    }),
  ];

  useEffect(() => {
    const fetchCurrentAvatar  = async () => {
      try {
        const res = await fetchUserProfile();
        // Adjust if your backend stores relative path
        const avatarPath = res.data.avatarStyle;
        setCurrentAvatar(avatarPath ? `${BASE_BACKEND_URL}${avatarPath.startsWith('/static/avatars/') ? '' : '/static/avatars/'}${avatarPath.split('/').pop()}` : null);
        // OR if backend stores full URL:
        // setCurrentAvatar(res.data.avatarStyle || null);
      } catch (error) {
        console.error("Failed to fetch profile", error);
      }
    };
    fetchCurrentAvatar ();
  }, []);

  const handleUpdate = async () => {
    if (!selected) return;

    try {
      await updateUserProfile({ avatarStyle: selected });
      await fetchUser();
      setSuccessMsg("Avatar updated successfully!");
      setCurrentAvatar(selected);
      setSelected(null);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (error) {
      console.error(error);
      setSuccessMsg("Failed to update avatar");
      setTimeout(() => setSuccessMsg(""), 3000);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center gap-6 p-6 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-xl"
    >
      <motion.div 
        className="text-center"
        initial={{ y: -20 }}
        animate={{ y: 0 }}
      >
        <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
          Choose Your Avatar
        </h3>
        <p className="text-gray-600">Select an avatar to personalize your profile</p>
      </motion.div>

      {currentAvatar && (
        <motion.div 
          className="flex flex-col items-center"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <p className="mb-3 font-semibold text-gray-700">Current Avatar</p>
          <div className="relative">
            <img
              src={currentAvatar}
              alt="Current avatar"
              loading="eager"
              className="w-28 h-28 rounded-full object-cover ring-4 ring-blue-500 shadow-xl"
            />
            <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2 shadow-lg">
              <FiCheckCircle className="text-white w-5 h-5" />
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 w-full max-w-2xl">
        {avatarList.map((avatar, index) => (
          <motion.div
            key={index}
            onClick={() => setSelected(avatar.pngSrc)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className={`w-20 h-20 cursor-pointer relative rounded-full overflow-hidden transition-all duration-200
              ${selected === avatar.pngSrc ? "ring-4 ring-blue-600 shadow-xl" : "ring-2 ring-gray-200 hover:ring-blue-300"}`}
          >
            <img
              src={avatar.pngSrc}
              alt={avatar.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            {selected === avatar.pngSrc && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-blue-600 bg-opacity-20 flex items-center justify-center"
              >
                <FiCheckCircle className="text-white w-6 h-6" />
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {selected && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleUpdate}
          className="mt-4 px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg font-semibold flex items-center gap-2"
        >
          <FiSave className="w-5 h-5" />
          Update Avatar
        </motion.button>
      )}

      <AnimatePresence>
        {successMsg && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium flex items-center gap-2"
          >
            <FiCheckCircle className="w-5 h-5" />
            {successMsg}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};



export default function ProfilePage() {
    const navigate = useNavigate();
    const [activeSubTab, setActiveSubTab] = useState('profile');

    const [sidebarOpen, setSidebarOpen] = useState(false);


    const { user, isLoading, updateUser } = useAuth();

    // State for main profile details
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        preferredFullName: '',
        emailId: '',
        phoneNumber: '',
        dateOfBirth: '',
        gender: '',
        visaStatus: '',
  training: '',
  employmentType: '',
        // pronouns: '',
        profilePicture: null, // This will hold the URL/path if it's already on the server, or null
    });

    // State for address details
    const [addressData, setAddressData] = useState({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
    });

    // State for emergency contact details
    const [emergencyContactData, setEmergencyContactData] = useState({
        contactName: '',
        contactRelationship: '',
        contactPhone: '',
    });

    // State for social media links
    const [socialData, setSocialData] = useState({
        facebook: '',
        twitter: '',
        linkedin: '',
    });

    // State for selected file for upload
    const [selectedFile, setSelectedFile] = useState(null);

    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);


    // State to manage which tab is active: 'profile' or 'leaves'
    const [activeTab, setActiveTab] = useState('profile');

    // Effect to initialize state when user data is available or changes
    useEffect(() => {
        if (user) {
            setProfileData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                preferredFullName: user.preferredFullName || '',
                emailId: user.emailId || '',
                phoneNumber: user.phoneNumber || '',
                dateOfBirth: user.dateOfBirth ? formatDateForInput(user.dateOfBirth) : '',
                gender: user.gender || '',
                  visaStatus: user.visaStatus,
  training: user.training,
  employmentType: user.employmentType,
                // pronouns: user.pronouns || '',
                profilePicture: user.profilePicture || null, // Set existing profile picture URL
            });
            setAddressData(user.address || { addressLine1: '', addressLine2: '', city: '', state: '', postcode: '', country: '' });
            setEmergencyContactData(user.emergencyContact || { contactName: '', contactRelationship: '', contactPhone: '' });
            setSocialData(user.social || { facebook: '', twitter: '', linkedin: '' });
        }
    }, [user]);

    // Show loading state
    if (isLoading) {
        return <div className="text-center mt-20 text-lg">Loading profile...</div>;
    }

    // Redirect if no user is logged in
    if (!user) {
        navigate('/login');
        return null;
    }

    // Handler for profile picture file input change
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        console.log("[Frontend] Selected file in handleFileChange:", file);
        setError('');
    };

    // Generic handler for profileData changes
    const handleProfileDataChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Generic handler for addressData changes
    const handleAddressDataChange = (e) => {
        const { name, value } = e.target;
        setAddressData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Generic handler for emergencyContactData changes
    const handleEmergencyContactDataChange = (e) => {
        const { name, value } = e.target;
        setEmergencyContactData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Generic handler for socialData changes
    const handleSocialDataChange = (e) => {
        const { name, value } = e.target;
        setSocialData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handles the submission of the profile update form
    const handleProfileUpdate = async (event) => {
        event.preventDefault();
        setIsUpdating(true);
        setMessage('');
        setError('');

        const formData = new FormData();

        // Append profile data
        for (const key in profileData) {
            if (key === 'profilePicture') {
                if (selectedFile) { // Only append if a new file is selected for upload
                    formData.append('profilePicture', selectedFile);
                }
            } else {
                formData.append(key, profileData[key]);
            }
        }

        // Append address data with nested keys (e.g., 'address.addressLine1')
        for (const key in addressData) {
            formData.append(`address.${key}`, addressData[key]);
        }

        // Append emergency contact data with nested keys
        for (const key in emergencyContactData) {
            formData.append(`emergencyContact.${key}`, emergencyContactData[key]);
        }

        // Append social data with nested keys
        for (const key in socialData) {
            formData.append(`social.${key}`, socialData[key]);
        }

        console.log("[Frontend] FormData content before API call:");
        for (let [key, value] of formData.entries()) {
            console.log(`${key}:`, value);
        }

        try {
            const response = await updateUserProfile(formData);
            setIsUpdating(false);

            if (response.status === 200) {
                setMessage(response.data.message);
                console.log("ProfilePage: API success, response.data.user:", response.data.user);
                updateUser(response.data.user); // Update user in AuthContext

                // Re-initialize all state with the latest user data from the API response
                setProfileData({
                    firstName: response.data.user.firstName || '',
                    lastName: response.data.user.lastName || '',
                    preferredFullName: response.data.user.preferredFullName || '',
                    emailId: response.data.user.emailId || '',
                    phoneNumber: response.data.user.phoneNumber || '',
                    dateOfBirth: response.data.user.dateOfBirth ? formatDateForInput(response.data.user.dateOfBirth) : '',
                    gender: response.data.user.gender || '',
                    visaStatus: response.data.user.visaStatus,
  training: response.data.user.training,        
    employmentType: response.data.user.employmentType,
                    // pronouns: response.data.user.pronouns || '',
                    profilePicture: response.data.user.profilePicture || null,
                });
                setAddressData(response.data.user.address || { addressLine1: '', addressLine2: '', city: '', state: '', postcode: '', country: '' });
                setEmergencyContactData(response.data.user.emergencyContact || { contactName: '', contactRelationship: '', contactPhone: '' });
                setSocialData(response.data.user.social || { facebook: '', twitter: '', linkedin: '' });

                setSelectedFile(null); // Clear selected file after successful upload
                document.getElementById('profilePicture').value = ''; // Clear file input field
            } else {
                setError(response.data?.message || 'Failed to update profile.');
            }
        } catch (err) {
            setIsUpdating(false);
            const errMsg = err.response?.data?.message || 'Network error or server unavailable.';
            setError(errMsg);
            console.error('Profile update error:', err);
        }
    };

    return (
        <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20">
            {/* Sidebar */}
            {/* Mobile header with menu button */}
<div className="md:hidden flex items-center justify-between bg-white/80 backdrop-blur-sm p-4 shadow-md">
  <h2 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Dashboard</h2>
  <motion.button
    whileTap={{ scale: 0.95 }}
    onClick={() => setSidebarOpen(!sidebarOpen)}
    className="p-2 text-gray-700 hover:text-blue-600 focus:outline-none rounded-lg hover:bg-blue-50"
  >
    {sidebarOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
  </motion.button>
</div>

            
                <motion.aside
  initial={{ x: -280 }}
  animate={{ x: 0 }}
  className={`w-64 bg-white/90 backdrop-blur-sm text-gray-800 flex flex-col p-6 shadow-xl fixed md:static inset-y-0 left-0 transform 
    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
    md:translate-x-0 transition-transform duration-300 z-40 border-r border-gray-200`}
  style={{ width: '16rem' }}
>

                <div className="mb-8">
                    <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Dashboard</h3>
                    <nav>
                        <ul className="space-y-2">
                            <li>
                                <motion.button
                                    whileHover={{ x: 4 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setActiveTab('profile')}
                                    className={`w-full text-left py-3 px-4 rounded-xl transition-all duration-200 flex items-center gap-3 font-medium
                                        ${activeTab === 'profile' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg' : 'hover:bg-gray-100 text-gray-700'}`}
                                >
                                    <FiUser className="w-5 h-5" />
                                    User Profile
                                </motion.button>
                            </li>
                            <li>
                                <motion.button
                                    whileHover={{ x: 4 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setActiveTab('leaves')}
                                    className={`w-full text-left py-3 px-4 rounded-xl transition-all duration-200 flex items-center gap-3 font-medium
                                        ${activeTab === 'leaves' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg' : 'hover:bg-gray-100 text-gray-700'}`}
                                >
                                    <FiUmbrella className="w-5 h-5" />
                                    Leaves
                                </motion.button>
                            </li>
                            
                        </ul>
                    </nav>
                </div>
            </motion.aside>
            {sidebarOpen && (
  <div
    className="fixed inset-0 bg-black bg-opacity-40 md:hidden z-30"
    onClick={() => setSidebarOpen(false)}
  />
)}


            {/* Main Content Area */}
            <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
                {activeTab === 'profile' && (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="container mx-auto max-w-6xl"
                    >
                        <div className="mb-8">
                          <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Your Profile</h2>
                          <p className="text-gray-600">Manage your personal information and settings</p>
                        </div>
                         <div className="inline-flex bg-white/80 backdrop-blur-sm rounded-xl p-1.5 mb-8 shadow-md border border-gray-200">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveSubTab('profile')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeSubTab === 'profile'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <FiSettings className="w-4 h-4" />
              Profile
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveSubTab('avatar')}
              className={`px-6 py-3 ml-2 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeSubTab === 'avatar'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <FiImage className="w-4 h-4" />
              Avatar
            </motion.button>
          </div>

                        <AnimatePresence>
                          {message && (
                            <motion.div 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700 px-6 py-4 rounded-lg shadow-md mb-6 flex items-center gap-3"
                            >
                              <FiCheckCircle className="w-6 h-6 text-green-500" />
                              <span className="font-medium">{message}</span>
                            </motion.div>
                          )}
                          {error && (
                            <motion.div 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-lg shadow-md mb-6 flex items-center gap-3"
                            >
                              <FiAlertCircle className="w-6 h-6 text-red-500" />
                              <span className="font-medium">{error}</span>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {activeSubTab === 'profile' && (<form onSubmit={handleProfileUpdate} className="space-y-6">
                            {/* Profile Picture Section */}
                            <motion.div 
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 }}
                              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200/50"
                            >
                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                                  <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                                    <FiImage className="w-6 h-6 text-white" />
                                  </div>
                                  <h3 className="text-2xl font-bold text-gray-800">Profile Picture</h3>
                                </div>
                                <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
                                    {profileData.profilePicture ? (
                                        <div className="relative group">
                                          <img
                                              src={profileData.profilePicture}
                                              alt="Profile"
                                              className="w-36 h-36 rounded-full object-cover ring-4 ring-blue-500 shadow-2xl"
                                          />
                                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-full transition-all duration-200" />
                                        </div>
                                    ) : (
                                        <div className="w-36 h-36 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-500 shadow-lg ring-4 ring-gray-300">
                                            <FiUser className="w-12 h-12" />
                                        </div>
                                    )}
                                    <div className="flex-1 w-full">
                                        <label htmlFor="profilePicture" className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                          <FiImage className="w-4 h-4 text-blue-600" />
                                          Upload New Profile Picture
                                        </label>
                                        <input
                                            type="file"
                                            id="profilePicture"
                                            onChange={handleFileChange}
                                            className="block w-full text-sm text-gray-900 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-blue-600 file:to-purple-600 file:text-white hover:file:from-blue-700 hover:file:to-purple-700 cursor-pointer file:shadow-lg transition-all"
                                        />
                                        {selectedFile && (
                                          <motion.p 
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-gray-600 text-sm mt-3 flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg"
                                          >
                                            <FiCheckCircle className="w-4 h-4 text-blue-600" />
                                            Selected: <span className="font-medium">{selectedFile.name}</span>
                                          </motion.p>
                                        )}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Personal Information Section */}
                            <motion.div 
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200/50"
                            >
                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                                  <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                                    <FiUser className="w-6 h-6 text-white" />
                                  </div>
                                  <h3 className="text-2xl font-bold text-gray-800">Personal Information</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group">
                                        <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiUser className="w-4 h-4 text-purple-600" />
                                          First Name
                                        </label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            value={profileData.firstName}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiUser className="w-4 h-4 text-purple-600" />
                                          Last Name
                                        </label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            value={profileData.lastName}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="preferredFullName" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiUserCheck className="w-4 h-4 text-purple-600" />
                                          Preferred Full Name
                                        </label>
                                        <input
                                            type="text"
                                            id="preferredFullName"
                                            name="preferredFullName"
                                            value={profileData.preferredFullName}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="emailId" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiMail className="w-4 h-4 text-purple-600" />
                                          Email ID
                                        </label>
                                        <input
                                            type="email"
                                            id="emailId"
                                            name="emailId"
                                            value={profileData.emailId}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 bg-gray-50 focus:outline-none cursor-not-allowed"
                                            disabled // Email ID is often not editable
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiPhone className="w-4 h-4 text-purple-600" />
                                          Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            value={profileData.phoneNumber}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="dateOfBirth" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiCalendar className="w-4 h-4 text-purple-600" />
                                          Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            id="dateOfBirth"
                                            name="dateOfBirth"
                                            value={profileData.dateOfBirth}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="gender" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiUser className="w-4 h-4 text-purple-600" />
                                          Gender
                                        </label>
                                        <select
                                            id="gender"
                                            name="gender"
                                            value={profileData.gender}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                            <option value="Non-binary">Non-binary</option>
                                            <option value="Prefer not to say">Prefer not to say</option>
                                        </select>
                                    </div>
                                                                        <div className="group">
            <label htmlFor="visaStatus" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FiGlobe className="w-4 h-4 text-purple-600" />
              Visa Status
            </label>
            <input
                type="text"
                id="visaStatus"
                name="visaStatus"
                value={profileData.visaStatus}
                onChange={handleProfileDataChange}
                className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
            />
        </div>
          <div className="group">
            <label htmlFor="training" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FiCheckCircle className="w-4 h-4 text-purple-600" />
              Training
            </label>
            <input
                type="text"
                id="training"
                name="training"
                value={profileData.training}
                onChange={handleProfileDataChange}
                className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
            />
        </div>
        <div className="group">
            <label htmlFor="employmentType" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FiUser className="w-4 h-4 text-purple-600" />
              Employment Type
            </label>
            <input
                type="text"
                id="employmentType"
                name="employmentType"
                value={profileData.employmentType}
                onChange={handleProfileDataChange}
                className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
            />
        </div>
                                    <div>
                                        {/* <label htmlFor="pronouns" className="block text-sm font-medium text-gray-700 mb-1">Pronouns</label>
                                        <input
                                            type="text"
                                            id="pronouns"
                                            name="pronouns"
                                            value={profileData.pronouns}
                                            onChange={handleProfileDataChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="e.g., He/Him, She/Her, They/Them"
                                        /> */}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Address Information Section */}
                            <motion.div 
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3 }}
                              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200/50"
                            >
                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                                    <FiMapPin className="w-6 h-6 text-white" />
                                  </div>
                                  <h3 className="text-2xl font-bold text-gray-800">Address Information</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group md:col-span-2">
                                        <label htmlFor="addressLine1" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiMapPin className="w-4 h-4 text-green-600" />
                                          Address Line 1
                                        </label>
                                        <input
                                            type="text"
                                            id="addressLine1"
                                            name="addressLine1"
                                            value={addressData.addressLine1}
                                            onChange={handleAddressDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-green-300"
                                        />
                                    </div>
                                    <div className="group md:col-span-2">
                                        <label htmlFor="addressLine2" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiMapPin className="w-4 h-4 text-green-600" />
                                          Address Line 2
                                        </label>
                                        <input
                                            type="text"
                                            id="addressLine2"
                                            name="addressLine2"
                                            value={addressData.addressLine2}
                                            onChange={handleAddressDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-green-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiMapPin className="w-4 h-4 text-green-600" />
                                          City
                                        </label>
                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            value={addressData.city}
                                            onChange={handleAddressDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-green-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiMapPin className="w-4 h-4 text-green-600" />
                                          State
                                        </label>
                                        <input
                                            type="text"
                                            id="state"
                                            name="state"
                                            value={addressData.state}
                                            onChange={handleAddressDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-green-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="postcode" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiMapPin className="w-4 h-4 text-green-600" />
                                          Postcode
                                        </label>
                                        <input
                                            type="text"
                                            id="postcode"
                                            name="postcode"
                                            value={addressData.postcode}
                                            onChange={handleAddressDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-green-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiGlobe className="w-4 h-4 text-green-600" />
                                          Country
                                        </label>
                                        <input
                                            type="text"
                                            id="country"
                                            name="country"
                                            value={addressData.country}
                                            onChange={handleAddressDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-green-300"
                                        />
                                    </div>
                                </div>
                            </motion.div>

                            {/* Emergency Contact Information Section */}
                            <motion.div 
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 }}
                              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200/50"
                            >
                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                                  <div className="p-2 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg">
                                    <FiHeart className="w-6 h-6 text-white" />
                                  </div>
                                  <h3 className="text-2xl font-bold text-gray-800">Emergency Contact</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group md:col-span-2">
                                        <label htmlFor="contactName" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiUser className="w-4 h-4 text-red-600" />
                                          Contact Name
                                        </label>
                                        <input
                                            type="text"
                                            id="contactName"
                                            name="contactName"
                                            value={emergencyContactData.contactName}
                                            onChange={handleEmergencyContactDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 hover:border-red-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="contactRelationship" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiHeart className="w-4 h-4 text-red-600" />
                                          Relationship
                                        </label>
                                        <input
                                            type="text"
                                            id="contactRelationship"
                                            name="contactRelationship"
                                            value={emergencyContactData.contactRelationship}
                                            onChange={handleEmergencyContactDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 hover:border-red-300"
                                        />
                                    </div>
                                    <div className="group">
                                        <label htmlFor="contactPhone" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                          <FiPhone className="w-4 h-4 text-red-600" />
                                          Contact Phone
                                        </label>
                                        <input
                                            type="tel"
                                            id="contactPhone"
                                            name="contactPhone"
                                            value={emergencyContactData.contactPhone}
                                            onChange={handleEmergencyContactDataChange}
                                            className="mt-1 block w-full border-2 border-gray-200 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 hover:border-red-300"
                                        />
                                    </div>
                                </div>
                            </motion.div>

                            {/* Social Media Links Section */}
                            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
                                <h3 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-3">Social Media</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                                        <input
                                            type="text"
                                            id="facebook"
                                            name="facebook"
                                            value={socialData.facebook}
                                            onChange={handleSocialDataChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Facebook Profile URL"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                                        <input
                                            type="text"
                                            id="twitter"
                                            name="twitter"
                                            value={socialData.twitter}
                                            onChange={handleSocialDataChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Twitter Profile URL"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                                        <input
                                            type="text"
                                            id="linkedin"
                                            name="linkedin"
                                            value={socialData.linkedin}
                                            onChange={handleSocialDataChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="LinkedIn Profile URL"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Update Button */}
                            <motion.div 
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.5 }}
                              className="flex justify-end pt-6"
                            >
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    type="submit"
                                    className="inline-flex items-center gap-3 px-8 py-4 border border-transparent text-base font-bold rounded-xl shadow-lg text-white bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? (
                                        <>
                                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                          <span>Updating...</span>
                                        </>
                                    ) : (
                                        <>
                                          <FiSave className="w-5 h-5" />
                                          <span>Update Profile</span>
                                        </>
                                    )}
                                </motion.button>
                            </motion.div>
                        </form>
                        )}
                        {activeSubTab === 'avatar' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200/50"
            >
              <AvatarStyle />
            </motion.div>
          )}
                    </motion.div>
                )}
                {activeTab === 'leaves' && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <LeaveApplicationForm />
                    </motion.div>
                )}
                {/* {activeTab === 'avatar' &&(
                    <AvatarStyle/>
                )} */}
            </main>
        </div>
    );
}




// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { updateUserProfile } from '../api'; // Assuming you have this API call
// import { useAuth } from '../context/AuthContext';

// // Import the LeaveApplicationForm component
// import LeaveApplicationForm from './LeaveApplicationForm';

// // Helper function to format date for input[type="date"]
// const formatDateForInput = (dateString) => {
//     if (!dateString) return '';
//     const date = new Date(dateString);
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
// };

// export default function ProfilePage() {
//     const navigate = useNavigate();
//     const { user, isLoading, updateUser } = useAuth();

//     // State for main profile details
//     const [profileData, setProfileData] = useState({
//         firstName: '',
//         lastName: '',
//         preferredFullName: '',
//         emailId: '',
//         phoneNumber: '',
//         dateOfBirth: '',
//         gender: '',
//         visaStatus: '',
//   training: '',
//   employmentType: '',
//         // pronouns: '',
//         profilePicture: null, // This will hold the URL/path if it's already on the server, or null
//     });

//     // State for address details
//     const [addressData, setAddressData] = useState({
//         addressLine1: '',
//         addressLine2: '',
//         city: '',
//         state: '',
//         postcode: '',
//         country: '',
//     });

//     // State for emergency contact details
//     const [emergencyContactData, setEmergencyContactData] = useState({
//         contactName: '',
//         contactRelationship: '',
//         contactPhone: '',
//     });

//     // State for social media links
//     const [socialData, setSocialData] = useState({
//         facebook: '',
//         twitter: '',
//         linkedin: '',
//     });

//     // State for selected file for upload
//     const [selectedFile, setSelectedFile] = useState(null);

//     const [message, setMessage] = useState('');
//     const [error, setError] = useState('');
//     const [isUpdating, setIsUpdating] = useState(false);

//     // State to manage which tab is active: 'profile' or 'leaves'
//     const [activeTab, setActiveTab] = useState('profile');

//     // Effect to initialize state when user data is available or changes
//     useEffect(() => {
//         if (user) {
//             setProfileData({
//                 firstName: user.firstName || '',
//                 lastName: user.lastName || '',
//                 preferredFullName: user.preferredFullName || '',
//                 emailId: user.emailId || '',
//                 phoneNumber: user.phoneNumber || '',
//                 dateOfBirth: user.dateOfBirth ? formatDateForInput(user.dateOfBirth) : '',
//                 gender: user.gender || '',
//                   visaStatus: user.visaStatus,
//   training: user.training,
//   employmentType: user.employmentType,
//                 // pronouns: user.pronouns || '',
//                 profilePicture: user.profilePicture || null, // Set existing profile picture URL
//             });
//             setAddressData(user.address || { addressLine1: '', addressLine2: '', city: '', state: '', postcode: '', country: '' });
//             setEmergencyContactData(user.emergencyContact || { contactName: '', contactRelationship: '', contactPhone: '' });
//             setSocialData(user.social || { facebook: '', twitter: '', linkedin: '' });
//         }
//     }, [user]);

//     // Show loading state
//     if (isLoading) {
//         return <div className="text-center mt-20 text-lg">Loading profile...</div>;
//     }

//     // Redirect if no user is logged in
//     if (!user) {
//         navigate('/login');
//         return null;
//     }

//     // Handler for profile picture file input change
//     const handleFileChange = (event) => {
//         const file = event.target.files[0];
//         setSelectedFile(file);
//         console.log("[Frontend] Selected file in handleFileChange:", file);
//         setError('');
//     };

//     // Generic handler for profileData changes
//     const handleProfileDataChange = (e) => {
//         const { name, value } = e.target;
//         setProfileData(prevData => ({
//             ...prevData,
//             [name]: value
//         }));
//     };

//     // Generic handler for addressData changes
//     const handleAddressDataChange = (e) => {
//         const { name, value } = e.target;
//         setAddressData(prevData => ({
//             ...prevData,
//             [name]: value
//         }));
//     };

//     // Generic handler for emergencyContactData changes
//     const handleEmergencyContactDataChange = (e) => {
//         const { name, value } = e.target;
//         setEmergencyContactData(prevData => ({
//             ...prevData,
//             [name]: value
//         }));
//     };

//     // Generic handler for socialData changes
//     const handleSocialDataChange = (e) => {
//         const { name, value } = e.target;
//         setSocialData(prevData => ({
//             ...prevData,
//             [name]: value
//         }));
//     };

//     // Handles the submission of the profile update form
//     const handleProfileUpdate = async (event) => {
//         event.preventDefault();
//         setIsUpdating(true);
//         setMessage('');
//         setError('');

//         const formData = new FormData();

//         // Append profile data
//         for (const key in profileData) {
//             if (key === 'profilePicture') {
//                 if (selectedFile) { // Only append if a new file is selected for upload
//                     formData.append('profilePicture', selectedFile);
//                 }
//             } else {
//                 formData.append(key, profileData[key]);
//             }
//         }

//         // Append address data with nested keys (e.g., 'address.addressLine1')
//         for (const key in addressData) {
//             formData.append(`address.${key}`, addressData[key]);
//         }

//         // Append emergency contact data with nested keys
//         for (const key in emergencyContactData) {
//             formData.append(`emergencyContact.${key}`, emergencyContactData[key]);
//         }

//         // Append social data with nested keys
//         for (const key in socialData) {
//             formData.append(`social.${key}`, socialData[key]);
//         }

//         console.log("[Frontend] FormData content before API call:");
//         for (let [key, value] of formData.entries()) {
//             console.log(`${key}:`, value);
//         }

//         try {
//             const response = await updateUserProfile(formData);
//             setIsUpdating(false);

//             if (response.status === 200) {
//                 setMessage(response.data.message);
//                 console.log("ProfilePage: API success, response.data.user:", response.data.user);
//                 updateUser(response.data.user); // Update user in AuthContext

//                 // Re-initialize all state with the latest user data from the API response
//                 setProfileData({
//                     firstName: response.data.user.firstName || '',
//                     lastName: response.data.user.lastName || '',
//                     preferredFullName: response.data.user.preferredFullName || '',
//                     emailId: response.data.user.emailId || '',
//                     phoneNumber: response.data.user.phoneNumber || '',
//                     dateOfBirth: response.data.user.dateOfBirth ? formatDateForInput(response.data.user.dateOfBirth) : '',
//                     gender: response.data.user.gender || '',
//                     visaStatus: response.data.user.visaStatus,
//   training: response.data.user.training,        
//     employmentType: response.data.user.employmentType,
//                     // pronouns: response.data.user.pronouns || '',
//                     profilePicture: response.data.user.profilePicture || null,
//                 });
//                 setAddressData(response.data.user.address || { addressLine1: '', addressLine2: '', city: '', state: '', postcode: '', country: '' });
//                 setEmergencyContactData(response.data.user.emergencyContact || { contactName: '', contactRelationship: '', contactPhone: '' });
//                 setSocialData(response.data.user.social || { facebook: '', twitter: '', linkedin: '' });

//                 setSelectedFile(null); // Clear selected file after successful upload
//                 document.getElementById('profilePicture').value = ''; // Clear file input field
//             } else {
//                 setError(response.data?.message || 'Failed to update profile.');
//             }
//         } catch (err) {
//             setIsUpdating(false);
//             const errMsg = err.response?.data?.message || 'Network error or server unavailable.';
//             setError(errMsg);
//             console.error('Profile update error:', err);
//         }
//     };

//     return (
//         <div className="flex min-h-screen bg-gray-100">
//             {/* Sidebar */}
//             <aside className="w-64 bg-white text-gray-800 flex flex-col p-4 shadow-md">
//                 <div className="mb-8">
//                     <h3 className="text-xl font-semibold mb-4">Dashboard</h3>
//                     <nav>
//                         <ul>
//                             <li className="mb-2">
//                                 <button
//                                     onClick={() => setActiveTab('profile')}
//                                     className={`w-full text-left py-2 px-4 rounded-md transition-colors duration-200
//                                         ${activeTab === 'profile' ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`}
//                                 >
//                                     User Profile
//                                 </button>
//                             </li>
//                             <li className="mb-2">
//                                 <button
//                                     onClick={() => setActiveTab('leaves')}
//                                     className={`w-full text-left py-2 px-4 rounded-md transition-colors duration-200
//                                         ${activeTab === 'leaves' ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`}
//                                 >
//                                     Leaves
//                                 </button>
//                             </li>
//                         </ul>
//                     </nav>
//                 </div>
//             </aside>

//             {/* Main Content Area */}
//             <main className="flex-1 p-6 overflow-y-auto">
//                 {activeTab === 'profile' && (
//                     <div className="container mx-auto p-4">
//                         <h2 className="text-3xl font-extrabold mb-8 text-gray-900">Your Profile</h2>

//                         {message && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">{message}</div>}
//                         {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">{error}</div>}

//                         <form onSubmit={handleProfileUpdate} className="space-y-8">
//                             {/* Profile Picture Section */}
//                             <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
//                                 <h3 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-3">Profile Picture</h3>
//                                 <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8">
//                                     {profileData.profilePicture ? (
//                                         <img
//                                             src={profileData.profilePicture}
//                                             alt="Profile"
//                                             className="w-32 h-32 rounded-full object-cover border-4 border-blue-400 shadow-md"
//                                         />
//                                     ) : (
//                                         <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm border-2 border-gray-300">
//                                             No Picture
//                                         </div>
//                                     )}
//                                     <div className="flex-1">
//                                         <label htmlFor="profilePicture" className="block text-sm font-medium text-gray-700 mb-2">Upload New Profile Picture</label>
//                                         <input
//                                             type="file"
//                                             id="profilePicture"
//                                             onChange={handleFileChange}
//                                             className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
//                                         />
//                                         {selectedFile && <p className="text-gray-500 text-xs mt-2">Selected: {selectedFile.name}</p>}
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Personal Information Section */}
//                             <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
//                                 <h3 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-3">Personal Information</h3>
//                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                                     <div>
//                                         <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
//                                         <input
//                                             type="text"
//                                             id="firstName"
//                                             name="firstName"
//                                             value={profileData.firstName}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
//                                         <input
//                                             type="text"
//                                             id="lastName"
//                                             name="lastName"
//                                             value={profileData.lastName}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="preferredFullName" className="block text-sm font-medium text-gray-700 mb-1">Preferred Full Name</label>
//                                         <input
//                                             type="text"
//                                             id="preferredFullName"
//                                             name="preferredFullName"
//                                             value={profileData.preferredFullName}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="emailId" className="block text-sm font-medium text-gray-700 mb-1">Email ID</label>
//                                         <input
//                                             type="email"
//                                             id="emailId"
//                                             name="emailId"
//                                             value={profileData.emailId}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 focus:outline-none"
//                                             disabled // Email ID is often not editable
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
//                                         <input
//                                             type="tel"
//                                             id="phoneNumber"
//                                             name="phoneNumber"
//                                             value={profileData.phoneNumber}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
//                                         <input
//                                             type="date"
//                                             id="dateOfBirth"
//                                             name="dateOfBirth"
//                                             value={profileData.dateOfBirth}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
//                                         <select
//                                             id="gender"
//                                             name="gender"
//                                             value={profileData.gender}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         >
//                                             <option value="">Select Gender</option>
//                                             <option value="Male">Male</option>
//                                             <option value="Female">Female</option>
//                                             <option value="Non-binary">Non-binary</option>
//                                             <option value="Prefer not to say">Prefer not to say</option>
//                                         </select>
//                                     </div>
//                                                                         <div>
//             <label htmlFor="visaStatus" className="block text-sm font-medium text-gray-700 mb-1">Visa Status</label>
//             <input
//                 type="text"
//                 id="visaStatus"
//                 name="visaStatus"
//                 value={profileData.visaStatus}
//                 onChange={handleProfileDataChange}
//                 className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//             />
//         </div>
//           <div>
//             <label htmlFor="training" className="block text-sm font-medium text-gray-700 mb-1">Training</label>
//             <input
//                 type="text"
//                 id="training"
//                 name="training"
//                 value={profileData.training}
//                 onChange={handleProfileDataChange}
//                 className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//             />
//         </div>
//         <div>
//             <label htmlFor="employmentType" className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
//             <input
//                 type="text"
//                 id="employmentType"
//                 name="employmentType"
//                 value={profileData.employmentType}
//                 onChange={handleProfileDataChange}
//                 className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//             />
//         </div>
//                                     <div>
//                                         {/* <label htmlFor="pronouns" className="block text-sm font-medium text-gray-700 mb-1">Pronouns</label>
//                                         <input
//                                             type="text"
//                                             id="pronouns"
//                                             name="pronouns"
//                                             value={profileData.pronouns}
//                                             onChange={handleProfileDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                             placeholder="e.g., He/Him, She/Her, They/Them"
//                                         /> */}
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Address Information Section */}
//                             <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
//                                 <h3 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-3">Address Information</h3>
//                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                                     <div>
//                                         <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
//                                         <input
//                                             type="text"
//                                             id="addressLine1"
//                                             name="addressLine1"
//                                             value={addressData.addressLine1}
//                                             onChange={handleAddressDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
//                                         <input
//                                             type="text"
//                                             id="addressLine2"
//                                             name="addressLine2"
//                                             value={addressData.addressLine2}
//                                             onChange={handleAddressDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">City</label>
//                                         <input
//                                             type="text"
//                                             id="city"
//                                             name="city"
//                                             value={addressData.city}
//                                             onChange={handleAddressDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">State</label>
//                                         <input
//                                             type="text"
//                                             id="state"
//                                             name="state"
//                                             value={addressData.state}
//                                             onChange={handleAddressDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="postcode" className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
//                                         <input
//                                             type="text"
//                                             id="postcode"
//                                             name="postcode"
//                                             value={addressData.postcode}
//                                             onChange={handleAddressDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
//                                         <input
//                                             type="text"
//                                             id="country"
//                                             name="country"
//                                             value={addressData.country}
//                                             onChange={handleAddressDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Emergency Contact Information Section */}
//                             <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
//                                 <h3 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-3">Emergency Contact</h3>
//                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                                     <div>
//                                         <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
//                                         <input
//                                             type="text"
//                                             id="contactName"
//                                             name="contactName"
//                                             value={emergencyContactData.contactName}
//                                             onChange={handleEmergencyContactDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="contactRelationship" className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
//                                         <input
//                                             type="text"
//                                             id="contactRelationship"
//                                             name="contactRelationship"
//                                             value={emergencyContactData.contactRelationship}
//                                             onChange={handleEmergencyContactDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
//                                         <input
//                                             type="tel"
//                                             id="contactPhone"
//                                             name="contactPhone"
//                                             value={emergencyContactData.contactPhone}
//                                             onChange={handleEmergencyContactDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                         />
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Social Media Links Section */}
//                             <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
//                                 <h3 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-3">Social Media</h3>
//                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                                     <div>
//                                         <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
//                                         <input
//                                             type="text"
//                                             id="facebook"
//                                             name="facebook"
//                                             value={socialData.facebook}
//                                             onChange={handleSocialDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                             placeholder="Facebook Profile URL"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
//                                         <input
//                                             type="text"
//                                             id="twitter"
//                                             name="twitter"
//                                             value={socialData.twitter}
//                                             onChange={handleSocialDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                             placeholder="Twitter Profile URL"
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
//                                         <input
//                                             type="text"
//                                             id="linkedin"
//                                             name="linkedin"
//                                             value={socialData.linkedin}
//                                             onChange={handleSocialDataChange}
//                                             className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                                             placeholder="LinkedIn Profile URL"
//                                         />
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Update Button */}
//                             <div className="flex justify-end pt-4">
//                                 <button
//                                     type="submit"
//                                     className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
//                                     disabled={isUpdating}
//                                 >
//                                     {isUpdating ? (
//                                         <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                                         </svg>
//                                     ) : (
//                                         'Update Profile'
//                                     )}
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 )}
//                 {activeTab === 'leaves' && (
//                     <LeaveApplicationForm />
//                 )}
//             </main>
//         </div>
//     );
// }

